import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useT, useLocale } from '../i18n/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../data/DataContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Callout from '../components/ui/Callout';
import StatCard from '../components/ui/StatCard';
import DataTable from '../components/ui/DataTable';
import EmptyState from '../components/ui/EmptyState';
import SearchRow, { SearchInput } from '../components/ui/SearchRow';
import Select from '../components/ui/Select';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import ReservationDetailModal from '../components/reservations/ReservationDetailModal';
import { RESERVATION_STATUSES, RESERVATION_STATUS_TONE } from '../components/reservations/constants';
import { useApi } from '../api/client';
import {
  cancelReservation,
  getReservationSummary,
  listReservations,
  recheckReservation,
} from '../api/reservations';
import useDebounce from '../hooks/useDebounce';
import { formatCurrency, formatDate, formatNumber } from '../utils/format';

export default function ReservationsPage() {
  const t = useT();
  const { locale } = useLocale();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const { events } = useData();
  const canUpdate = hasPermission('reservations:update');

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [size] = useState(20);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [eventId, setEventId] = useState('');
  const debouncedQuery = useDebounce(query, 250);

  const [viewing, setViewing] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [rechecking, setRechecking] = useState(null);

  const loadSummary = useCallback(async () => {
    if (!useApi) return;
    try {
      setSummary(await getReservationSummary());
    } catch {
      // The table load reports errors; the cards just stay empty.
    }
  }, []);

  const load = useCallback(async () => {
    if (!useApi) return;
    setLoading(true);
    try {
      const data = await listReservations({ q: debouncedQuery, status, eventId, page, size });
      setRows(data.content);
      setTotal(data.totalElements || 0);
      setTotalPages(data.totalPages || 0);
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, status, eventId, page, size, toast, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { setPage(0); }, [debouncedQuery, status, eventId]);

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('reservations_page.filter_status') },
      ...RESERVATION_STATUSES.map((s) => ({ value: s, label: t(`reservations_page.statuses.${s}`) })),
    ],
    [t]
  );

  const eventOptions = useMemo(
    () => [
      { value: '', label: t('reservations_page.filter_event') },
      ...[...events]
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .map((e) => ({ value: e.id, label: e.name })),
    ],
    [events, t]
  );

  if (!useApi) {
    return (
      <Card title={t('pages.reservations.title')} subtitle={t('pages.reservations.subtitle')}>
        <EmptyState icon="🔌" title={t('common.api_required')} />
      </Card>
    );
  }

  const replaceRow = (fresh) => {
    setRows((prev) => prev.map((r) => (r.id === fresh.id ? fresh : r)));
    setViewing((prev) => (prev && prev.id === fresh.id ? fresh : prev));
  };

  const handleRecheck = async (r) => {
    setRechecking(r.id);
    try {
      const fresh = await recheckReservation(r.id);
      replaceRow(fresh);
      toast.success(t('reservations_page.toast_rechecked', {
        code: fresh.code,
        status: t(`reservations_page.statuses.${fresh.status}`),
      }));
      loadSummary();
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setRechecking(null);
    }
  };

  const handleCancel = async () => {
    if (!cancelling) return;
    try {
      const fresh = await cancelReservation(cancelling.id);
      replaceRow(fresh);
      toast.success(t('reservations_page.toast_cancelled', { code: fresh.code }));
      loadSummary();
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setCancelling(null);
    }
  };

  const columns = [
    { key: 'code', header: t('common.reference'), cellClassName: 'mono', render: (r) => r.code },
    {
      key: 'contact',
      header: t('reservations_page.field_contact'),
      render: (r) => (
        <div className="cell-stack">
          <strong>{r.contactFirstName} {r.contactLastName}</strong>
          {r.contactEmail && <span className="cell-sub">{r.contactEmail}</span>}
        </div>
      ),
    },
    { key: 'phone', header: t('common.phone'), render: (r) => r.contactPhone || '—' },
    { key: 'payer', header: t('reservations_page.field_payer'), cellClassName: 'mono', render: (r) => r.payerMsisdn || '—' },
    {
      key: 'event',
      header: t('reservations_page.field_pilgrimage'),
      render: (r) => (
        <div className="cell-stack">
          <span>{r.eventName || '—'}</span>
          {r.eventDate && <span className="cell-sub">{formatDate(r.eventDate, t)}</span>}
        </div>
      ),
    },
    { key: 'people', header: t('reservations_page.field_people'), render: (r) => r.peopleCount },
    { key: 'total', header: t('common.total'), render: (r) => <strong>{formatCurrency(r.totalAmount, locale)}</strong> },
    { key: 'method', header: t('common.method'), render: (r) => t(`reservations_page.methods.${r.payMethod}`) },
    {
      key: 'status',
      header: t('common.status'),
      render: (r) => <Badge tone={RESERVATION_STATUS_TONE[r.status]}>{t(`reservations_page.statuses.${r.status}`)}</Badge>,
    },
    { key: 'date', header: t('common.date'), render: (r) => formatDate(r.createdAt, t) },
    {
      key: 'actions',
      header: t('common.actions'),
      render: (r) => (
        <div className="action-row">
          <button type="button" className="action-btn" onClick={() => setViewing(r)}>{t('common.view')}</button>
          {canUpdate && r.status === 'pending_payment' && (
            <button type="button" className="action-btn" onClick={() => handleRecheck(r)} disabled={rechecking === r.id}>
              {rechecking === r.id ? t('common.loading') : t('reservations_page.recheck')}
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      {summary && summary.gatewayConfigured === false && (
        <Callout tone="warning" title={t('reservations_page.gateway_missing_title')}>
          {t('reservations_page.gateway_missing_body')}
        </Callout>
      )}

      <div className="stats-grid stats-grid--5">
        <StatCard
          label={t('reservations_page.stat_confirmed')}
          value={formatNumber(summary?.confirmed || 0, locale)}
          change={t('reservations_page.stat_confirmed_caption')}
          icon="✅"
          tone="sc4"
        />
        <StatCard
          label={t('reservations_page.stat_pending')}
          value={formatNumber(summary?.pending_payment || 0, locale)}
          change={t('reservations_page.stat_pending_caption')}
          changeTone="warn"
          icon="⏳"
          tone="sc2"
        />
        <StatCard
          label={t('reservations_page.stat_failed')}
          value={formatNumber(summary?.failed || 0, locale)}
          change={t('reservations_page.stat_failed_caption', { count: summary?.cancelled || 0 })}
          changeTone="warn"
          icon="⚠"
          tone="sc3"
        />
        <StatCard
          label={t('reservations_page.stat_collected')}
          value={formatNumber(summary?.collectedAmount || 0, locale)}
          change={t('reservations_page.stat_collected_caption')}
          icon="💳"
          tone="sc1"
        />
        <StatCard
          label={t('reservations_page.stat_travellers')}
          value={formatNumber(summary?.confirmedPeople || 0, locale)}
          change={t('reservations_page.stat_travellers_caption')}
          icon="🚌"
          tone="sc4"
        />
      </div>

      <SearchRow>
        <SearchInput value={query} onChange={setQuery} placeholder={t('reservations_page.search_placeholder')} />
        <Select value={status} onChange={(e) => setStatus(e.target.value)} options={statusOptions} className="filter-select" />
        <Select value={eventId} onChange={(e) => setEventId(e.target.value)} options={eventOptions} className="filter-select" />
        <Button variant="ghost" onClick={() => { load(); loadSummary(); }} loading={loading}>{t('common.refresh')}</Button>
      </SearchRow>

      <Card title={t('reservations_page.title')} subtitle={t('reservations_page.subtitle')} padded={false}>
        <DataTable
          columns={columns}
          rows={rows}
          empty={(
            <EmptyState
              icon="🎟️"
              title={loading ? t('common.loading') : t('reservations_page.empty_title')}
              body={loading ? null : t('reservations_page.empty_body')}
            />
          )}
        />
        {total > 0 && (
          <div className="pagination">
            <div className="results-meta">
              {t('common.showing_results', {
                from: page * size + 1,
                to: Math.min(total, (page + 1) * size),
                total,
              })}
            </div>
            <div className="pagination-controls">
              <Button size="sm" variant="ghost" disabled={page === 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                {t('common.previous')}
              </Button>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {t('common.page_of', { current: page + 1, total: Math.max(1, totalPages) })}
              </span>
              <Button size="sm" variant="ghost" disabled={page >= totalPages - 1 || loading} onClick={() => setPage((p) => p + 1)}>
                {t('common.next')}
              </Button>
            </div>
          </div>
        )}
      </Card>

      <ReservationDetailModal
        isOpen={Boolean(viewing) && !cancelling}
        onClose={() => setViewing(null)}
        reservation={viewing}
        canUpdate={canUpdate}
        rechecking={Boolean(viewing && rechecking === viewing.id)}
        onRecheck={() => viewing && handleRecheck(viewing)}
        onCancel={() => viewing && setCancelling(viewing)}
      />

      <ConfirmDialog
        isOpen={Boolean(cancelling)}
        onClose={() => setCancelling(null)}
        onConfirm={handleCancel}
        title={t('reservations_page.cancel_confirm_title')}
        body={cancelling ? t('reservations_page.cancel_confirm_body', { code: cancelling.code }) : ''}
        confirmLabel={t('reservations_page.cancel_reservation')}
        cancelLabel={t('common.back')}
      />
    </div>
  );
}
